import React, { useState } from 'react';
import { CHARACTERS, VEHICLES } from '../engine/GameEngine';
import {
  loadCoinBalance,
  saveCoinBalance,
  loadUnlockedItems,
  saveUnlockedItems,
  loadSelectedCharacter,
  saveSelectedCharacter,
  loadSelectedVehicle,
  saveSelectedVehicle,
} from '../engine/GameState';

export default function ShopScreen({ onClose }) {
  const [tab, setTab] = useState('characters');
  const [coinBalance, setCoinBalance] = useState(loadCoinBalance);
  const [unlocked, setUnlocked] = useState(loadUnlockedItems);
  const [selectedChar, setSelectedChar] = useState(loadSelectedCharacter);
  const [selectedVehicle, setSelectedVehicle] = useState(loadSelectedVehicle);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  };

  const handleUnlock = (item, type) => {
    if (coinBalance < item?.price) {
      showToast('Not enough coins!', 'error');
      return;
    }
    const newBalance = coinBalance - item?.price;
    const newUnlocked = { ...unlocked };
    if (type === 'character') {
      newUnlocked.characters = [...(newUnlocked?.characters || []), item?.id];
    } else {
      newUnlocked.vehicles = [...(newUnlocked?.vehicles || []), item?.id];
    }
    setCoinBalance(newBalance);
    saveCoinBalance(newBalance);
    setUnlocked(newUnlocked);
    saveUnlockedItems(newUnlocked);
    showToast(`${item?.name} unlocked! 🎉`);
  };

  const handleSelectChar = (id) => {
    setSelectedChar(id);
    saveSelectedCharacter(id);
    showToast('Character selected!');
  };

  const handleSelectVehicle = (id) => {
    setSelectedVehicle(id);
    saveSelectedVehicle(id);
    showToast('Vehicle selected!');
  };

  const isCharUnlocked = (id) => (unlocked?.characters || ['classic'])?.includes(id);
  const isVehicleUnlocked = (id) => (unlocked?.vehicles || ['scooter'])?.includes(id);

  const TABS = [
    { id: 'characters', label: 'Characters', emoji: '🐒' },
    { id: 'vehicles', label: 'Vehicles', emoji: '🛵' },
    { id: 'packs', label: 'Coin Packs', emoji: '🪙' },
  ];

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-md mx-4 rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a0a2e, #2d1a4e)',
          border: '2px solid rgba(99,102,241,0.5)',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div>
            <h2 className="font-heading font-bold text-xl text-white">🛒 Shop</h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-sm">🪙</span>
              <span className="font-data text-sm font-bold" style={{ color: '#f59e0b' }}>{coinBalance?.toLocaleString()}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {TABS?.map(t => (
            <button
              key={t?.id}
              onClick={() => setTab(t?.id)}
              className="flex-1 py-3 text-center text-sm font-semibold transition-all"
              style={{
                color: tab === t?.id ? '#6366f1' : 'rgba(255,255,255,0.5)',
                borderBottom: tab === t?.id ? '2px solid #6366f1' : '2px solid transparent',
              }}
            >
              {t?.emoji} {t?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'characters' && (
            <div className="grid grid-cols-2 gap-3">
              {CHARACTERS?.map(char => {
                const isUnlocked = isCharUnlocked(char?.id);
                const isSelected = selectedChar === char?.id;
                return (
                  <div
                    key={char?.id}
                    className="p-4 rounded-xl text-center transition-all"
                    style={{
                      background: isSelected ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                      border: isSelected ? '2px solid rgba(99,102,241,0.8)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <div className="text-4xl mb-2">{char?.emoji}</div>
                    <p className="font-body text-sm font-semibold text-white mb-1">{char?.name}</p>
                    {isUnlocked ? (
                      <button
                        onClick={() => handleSelectChar(char?.id)}
                        className="w-full py-1.5 rounded-lg text-xs font-bold transition-all"
                        style={{
                          background: isSelected ? 'rgba(99,102,241,0.5)' : 'rgba(16,185,129,0.3)',
                          color: isSelected ? '#a5b4fc' : '#34d399',
                          border: isSelected ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(16,185,129,0.4)',
                        }}
                      >
                        {isSelected ? '✓ Selected' : 'Select'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnlock(char, 'character')}
                        className="w-full py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
                        style={{ background: 'rgba(245,158,11,0.3)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)' }}
                      >
                        🪙 {char?.price}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'vehicles' && (
            <div className="grid grid-cols-2 gap-3">
              {VEHICLES?.map(vehicle => {
                const isUnlocked = isVehicleUnlocked(vehicle?.id);
                const isSelected = selectedVehicle === vehicle?.id;
                return (
                  <div
                    key={vehicle?.id}
                    className="p-4 rounded-xl text-center transition-all"
                    style={{
                      background: isSelected ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                      border: isSelected ? '2px solid rgba(99,102,241,0.8)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <div className="text-4xl mb-2">{vehicle?.emoji}</div>
                    <p className="font-body text-sm font-semibold text-white mb-0.5">{vehicle?.name}</p>
                    {vehicle?.speedBonus > 0 && (
                      <p className="font-caption text-xs mb-1" style={{ color: '#34d399' }}>+{vehicle?.speedBonus} Speed</p>
                    )}
                    {isUnlocked ? (
                      <button
                        onClick={() => handleSelectVehicle(vehicle?.id)}
                        className="w-full py-1.5 rounded-lg text-xs font-bold transition-all"
                        style={{
                          background: isSelected ? 'rgba(99,102,241,0.5)' : 'rgba(16,185,129,0.3)',
                          color: isSelected ? '#a5b4fc' : '#34d399',
                          border: isSelected ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(16,185,129,0.4)',
                        }}
                      >
                        {isSelected ? '✓ Selected' : 'Select'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnlock(vehicle, 'vehicle')}
                        className="w-full py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
                        style={{ background: 'rgba(245,158,11,0.3)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)' }}
                      >
                        🪙 {vehicle?.price}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'packs' && (
            <div className="space-y-3">
              <p className="font-caption text-xs text-center mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Coin packs (payments disabled — demo mode)</p>
              {[
                { name: 'Starter Pack', coins: 500, price: '$2.00', desc: '~30 min of gameplay', emoji: '🪙', color: '#f59e0b' },
                { name: 'Adventure Pack', coins: 2000, price: '$5.00', desc: '~1 week of gameplay', emoji: '💰', color: '#10b981' },
                { name: 'Pro Pack', coins: 5000, price: '$10.00', desc: 'Unlock everything!', emoji: '💎', color: '#6366f1' },
              ]?.map(pack => (
                <div
                  key={pack?.name}
                  className="p-4 rounded-xl flex items-center gap-4"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="text-3xl">{pack?.emoji}</div>
                  <div className="flex-1">
                    <p className="font-body text-sm font-bold text-white">{pack?.name}</p>
                    <p className="font-data text-lg font-black" style={{ color: pack?.color }}>🪙 {pack?.coins?.toLocaleString()}</p>
                    <p className="font-caption text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{pack?.desc}</p>
                  </div>
                  <button
                    className="px-4 py-2 rounded-xl text-sm font-bold opacity-50 cursor-not-allowed"
                    style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                    disabled
                  >
                    {pack?.price}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl text-sm font-bold text-white"
            style={{ background: toast?.type === 'error' ? 'rgba(239,68,68,0.9)' : 'rgba(16,185,129,0.9)' }}
          >
            {toast?.msg}
          </div>
        )}
      </div>
    </div>
  );
}
